import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BookOpen, ChevronLeft, FileText, ImagePlus, Layers, Loader2, LogOut, Paperclip, RefreshCw, Send, Settings, Sparkles, Users, Download,
} from 'lucide-react';
import type { HarnessEvent } from '@xiaotianshu/shared/harness';
import type { AgentBook, AgentDocument, AgentDraft, AgentIdea, AgentImage, AgentMessage, AgentThread } from '../../agent/agentClient';
import type { MobileChapter, MobileLibrary, MobileNotification } from '../../agent/mobileClient';
import {
  clearMobileToken, getMobileToken, mobileAcceptDraft, mobileAnswerApproval, mobileApproveIdea, mobileBooks,
  mobileChapter, mobileDrafts, mobileEnsureThread, mobileIdeas, mobileLibrary, mobileMessages,
  mobileRejectDraft, mobileRejectIdea, mobileSend, openMobileEvents,
  openMobileNotificationStream, pairDevice,
} from '../../agent/mobileClient';
import './mobile.css';
import { fileToChatImage } from '../../utils/chatImage';
import { fileToChatDocument } from '../../utils/chatFile';
import { useAutoGrow } from '../../hooks/useAutoGrow';
import { LinkifiedText } from '../../components/chat/LinkPreviewCard';
import { ApprovalDetails } from '../../components/chat/ApprovalDetails';
import { ImageViewer } from '../../components/chat/ImageViewer';
import { saveChatFile } from '../../utils/download';

function activityForTool(name: string): string {
  if (name === 'search_web') return '正在搜索网页…';
  if (name === 'browse_web') return '正在读网页…';
  if (name === 'see_image') return '正在看图片…';
  if (name === 'share_image') return '正在整理图片…';
  if (name === 'bookmark_web') return '正在收藏链接…';
  if (name === 'recall' || name === 'remember') return '正在回忆…';
  if (name === 'current_time') return '正在看时间…';
  if (name === 'propose_idea' || name === 'write_draft' || name === 'edit_chapter') return '正在整理想法…';
  if (name === 'execute') return '正在执行操作…';
  return '正在处理…';
}

function time(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

function shortTime(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

function plainText(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function MobilePage() {
  const [token, setToken] = useState(() => getMobileToken());
  const [cookieAuth, setCookieAuth] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [code, setCode] = useState('');
  const [deviceName, setDeviceName] = useState('我的手机');
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState('');

  const [books, setBooks] = useState<AgentBook[]>([]);
  const [bookId, setBookId] = useState('');
  const [thread, setThread] = useState<AgentThread | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [streaming, setStreaming] = useState('');
  const [activity, setActivity] = useState('');
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState<AgentImage[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<AgentDocument[]>([]);
  const inputRef = useAutoGrow(input, 120);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState<'chat' | 'feed' | 'works'>('chat');
  const [ideas, setIdeas] = useState<AgentIdea[]>([]);
  const [drafts, setDrafts] = useState<AgentDraft[]>([]);
  const [approval, setApproval] = useState<Extract<HarnessEvent, { type: 'approval-request' }> | null>(null);
  const [rejectingIdeaId, setRejectingIdeaId] = useState<string | null>(null);
  const [rejectText, setRejectText] = useState('');
  const [rejectingDraftId, setRejectingDraftId] = useState<string | null>(null);
  const [draftRejectText, setDraftRejectText] = useState('');
  const [expandedFeedId, setExpandedFeedId] = useState<string | null>(null);
  const [library, setLibrary] = useState<MobileLibrary | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [detail, setDetail] = useState<{ kind: 'chapter' | 'character' | 'faction' | 'setting' | 'item'; title: string; body: string } | null>(null);
  const [notice, setNotice] = useState<MobileNotification | null>(null);
  const [busy, setBusy] = useState(false);
  const [imageViewer, setImageViewer] = useState<{ src: string; name?: string } | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const sentAtRef = useRef(0);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<(() => void) | null>(null);

  const refreshIdeas = useCallback(async (id: string) => {
    try { setIdeas(await mobileIdeas(id)); } catch { /* ignore */ }
  }, []);
  const refreshDrafts = useCallback(async (id: string) => {
    try { setDrafts(await mobileDrafts(id)); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (token) { setCheckingAuth(false); return; }
    fetch('/api/auth/session', { credentials: 'include' })
      .then((res) => { if (res.ok) setCookieAuth(true); })
      .catch(() => { /* 未登录则显示配对页 */ })
      .finally(() => setCheckingAuth(false));
  }, [token]);

  useEffect(() => {
    if (!token && !cookieAuth) return;
    void (async () => {
      try {
        const rows = await mobileBooks();
        setBooks(rows);
        if (rows[0]) setBookId(rows[0].id);
      } catch (e) {
        setError(e instanceof Error ? e.message : '读取书架失败');
      }
    })();
  }, [token, cookieAuth]);

  useEffect(() => {
    if (!bookId || (!token && !cookieAuth)) return;
    let cancelled = false;
    setThread(null);
    setMessages([]);
    setStreaming('');
    streamRef.current?.();
    streamRef.current = null;
    void (async () => {
      try {
        const t = await mobileEnsureThread(bookId);
        if (cancelled) return;
        setThread(t);
        const [msgs] = await Promise.all([mobileMessages(t.id), refreshIdeas(bookId), refreshDrafts(bookId)]);
        if (cancelled) return;
        setMessages(msgs);
        streamRef.current = openMobileEvents(t.id, (ev: HarnessEvent) => {
          if (ev.type === 'text-delta') {
            setStreaming((v) => v + ev.text);
            setRunning(true);
            setActivity('正在写…');
          } else if (ev.type === 'tool-call') {
            setRunning(true);
            setActivity(activityForTool(ev.name));
          } else if (ev.type === 'tool-result') {
            setActivity('处理完了，继续…');
          } else if (ev.type === 'turn-start') {
            setRunning(true);
            setActivity('正在想…');
          } else if (ev.type === 'assistant-message') {
            setStreaming('');
            if (ev.text.trim()) setMessages((prev) => [...prev, {
              id: `${Date.now()}-${Math.random()}`, role: 'assistant', text: ev.text, created_at: new Date().toISOString(),
            }]);
          } else if (ev.type === 'turn-end') {
            setRunning(false);
            setActivity('');
            setStreaming('');
            void refreshIdeas(bookId);
            void refreshDrafts(bookId);
            void mobileMessages(t.id).then((rows) => setMessages(rows)).catch(() => { /* 忽略单次刷新失败 */ });
          } else if (ev.type === 'approval-request') {
            setActivity('等你确认…');
            setApproval(ev);
          } else if (ev.type === 'approval-answered') {
            setApproval((prev) => prev?.requestId === ev.requestId ? null : prev);
            setActivity('已经确认，继续…');
          } else if (ev.type === 'status' && ev.status === 'idle') {
            setRunning(false);
            setActivity('');
          } else if (ev.type === 'error') {
            setError(ev.message);
            setActivity('');
          }
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '连接失败');
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.();
      streamRef.current = null;
    };
  }, [bookId, token, refreshIdeas, refreshDrafts]);

  useEffect(() => {
    if (!token && !cookieAuth) return;
    const close = openMobileNotificationStream((n) => {
      if (document.visibilityState === 'hidden') {
        window.XiaotianshuAndroid?.notify(n.title, n.kind === 'message' ? '新消息' : n.kind);
      } else if (n.kind !== 'message') {
        setNotice(n);
      }
    });
    return close;
  }, [token]);

  useEffect(() => {
    if (!bookId || (!token && !cookieAuth) || tab !== 'works') return;
    let cancelled = false;
    setLibraryLoading(true);
    void mobileLibrary(bookId)
      .then((data) => { if (!cancelled) setLibrary(data); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : '读取作品失败'); })
      .finally(() => { if (!cancelled) setLibraryLoading(false); });
    return () => { cancelled = true; };
  }, [bookId, token, tab]);

  const scrollToLatest = (smooth = true) => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottomRef.current = true;
    setShowJump(false);
  };

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };

  useEffect(() => {
    if (tab !== 'chat') return;
    // 从作品/想法页返回聊天：默认落回最新一条，而不是第一条。
    atBottomRef.current = true;
    const id = requestAnimationFrame(() => scrollToLatest(false));
    return () => cancelAnimationFrame(id);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'chat') return;
    if (atBottomRef.current) {
      const id = requestAnimationFrame(() => scrollToLatest(false));
      return () => cancelAnimationFrame(id);
    }
    setShowJump(true);
    return undefined;
  }, [messages, streaming, tab]);

  const pair = async () => {
    setPairing(true);
    setError('');
    try {
      await pairDevice(code, deviceName);
      setToken(getMobileToken());
      setCode('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '配对失败');
    } finally { setPairing(false); }
  };

  useEffect(() => {
    if (!running || !thread) return;
    let cancelled = false;
    const check = async () => {
      try {
        const latest = await mobileMessages(thread.id);
        if (cancelled) return;
        setMessages((prev) => {
          const pendingLocals = prev.filter((m) => m.id.startsWith('local-')
            && !latest.some((s) => s.role === 'user' && s.text === m.text));
          return [...latest, ...pendingLocals];
        });
        const answered = latest.some((m) => m.role === 'assistant' && !m.shared
          && m.created_at && Date.parse(m.created_at) >= sentAtRef.current);
        if (answered) {
          setRunning(false);
          setActivity('');
          setStreaming('');
        }
      } catch { /* SSE 正常时轮询失败不影响 */ }
    };
    const timer = setInterval(() => void check(), 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [running, thread]);

  const addImages = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const next = [...pendingImages];
      for (const file of Array.from(files).slice(0, Math.max(0, 4 - next.length))) {
        next.push(await fileToChatImage(file));
      }
      setPendingImages(next);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '图片读取失败');
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const addDocuments = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const next = [...pendingDocuments];
      for (const file of Array.from(files).slice(0, Math.max(0, 4 - next.length))) {
        next.push(await fileToChatDocument(file));
      }
      setPendingDocuments(next);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '文档读取失败');
    } finally {
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  const send = async () => {
    const text = input.trim();
    const images = pendingImages;
    const documents = pendingDocuments;
    if ((!text && images.length === 0 && documents.length === 0) || !thread || running) return;
    setInput('');
    setPendingImages([]);
    setPendingDocuments([]);
    setRunning(true);
    setActivity('正在想…');
    sentAtRef.current = Date.now();
    setMessages((prev) => [...prev, {
      id: `local-${Date.now()}`, role: 'user', text, images, documents, created_at: new Date().toISOString(),
    }]);
    try { await mobileSend(thread.id, text, images, documents); } catch (e) {
      setRunning(false);
      setActivity('');
      setError(e instanceof Error ? e.message : '发送失败');
    }
  };

  const toggleFeed = (id: string, event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, textarea, input, a, summary, details, .m-reject-box')) return;
    setExpandedFeedId((prev) => prev === id ? null : id);
  };

  const approve = async (idea: AgentIdea) => {
    try { await mobileApproveIdea(idea.id); await refreshIdeas(idea.book_id); } catch (e) {
      setError(e instanceof Error ? e.message : '认可失败');
    }
  };

  const reject = async (idea: AgentIdea) => {
    await mobileRejectIdea(idea.id, rejectText.trim()).then(() => {
      setRejectingIdeaId(null);
      setRejectText('');
      return refreshIdeas(idea.book_id);
    }).catch((e) => {
      setError(e instanceof Error ? e.message : '拒绝失败');
    });
  };

  const rejectDraft = async (draft: AgentDraft) => {
    await mobileRejectDraft(draft.id, draftRejectText.trim()).then(() => {
      setRejectingDraftId(null);
      setDraftRejectText('');
      return refreshDrafts(draft.book_id);
    }).catch((e) => {
      setError(e instanceof Error ? e.message : '拒绝失败');
    });
  };

  const lookupApprovalTarget = (target: string, id: string | undefined): string | undefined => {
    if (!id || !library) return undefined;
    if (target === 'chapter' || target === 'chapter_content') {
      const ch = library.volumes.flatMap((v) => v.chapters).find((c) => c.id === id);
      return ch ? `第 ${ch.chapter_number ?? '?'} 章《${ch.title}》` : undefined;
    }
    if (target === 'volume') {
      const vol = library.volumes.find((v) => v.id === id);
      return vol ? `《${vol.title}》` : undefined;
    }
    if (target === 'character') return library.library.characters.find((x) => x.id === id)?.name;
    if (target === 'faction') return library.library.factions.find((x) => x.id === id)?.name;
    if (target === 'setting') return library.library.settings.find((x) => x.id === id)?.title;
    if (target === 'item') return library.library.items.find((x) => x.id === id)?.name;
    return undefined;
  };

  const answerApproval = async (decision: 'allow' | 'deny') => {    if (!approval || !thread) return;
    setBusy(true);
    try {
      await mobileAnswerApproval(thread.id, approval.requestId, decision);
      setApproval(null);
      if (decision === 'allow') {
        setDetail(null);
        void refreshLibrary();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '审批失败');
    } finally {
      setBusy(false);
    }
  };

  const openChapter = async (chapter: MobileChapter) => {
    if (!bookId) return;
    try {
      const data = await mobileChapter(bookId, chapter.id);
      setDetail({
        kind: 'chapter',
        title: `第 ${data.chapter_number ?? '?'} 章 ${data.title}`,
        body: plainText(data.content || '') || '（这一章还没有正文）',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : '读取章节失败');
    }
  };

  const refreshLibrary = async () => {
    if (!bookId) return;
    setLibraryLoading(true);
    try { setLibrary(await mobileLibrary(bookId)); } catch (e) {
      setError(e instanceof Error ? e.message : '读取作品失败');
    } finally { setLibraryLoading(false); }
  };

  const accept = async (draft: AgentDraft) => {
    if (!window.confirm(`接受《${draft.title ?? '未命名'}》并写入正文？`)) return;
    try { await mobileAcceptDraft(draft.id); await refreshDrafts(draft.book_id); } catch (e) {
      setError(e instanceof Error ? e.message : '接受失败');
    }
  };

  const feed = [
    ...ideas
      .filter((i) => i.status === 'proposed' || i.status === 'approved' || i.status === 'rejected')
      .map((i) => ({ kind: 'idea' as const, created: i.created_at, idea: i })),
    ...drafts
      .filter((d) => d.status === 'pending' || d.status === 'rejected')
      .map((d) => ({ kind: 'draft' as const, created: d.created_at, draft: d })),
  ].sort((a, b) => String(b.created).localeCompare(String(a.created)));

  if (checkingAuth) {
    return <div className="m-page m-pair-page"><div className="m-pair-card"><Loader2 className="spin" /><p>正在连接…</p></div></div>;
  }

  if (!token && !cookieAuth) {
    return (
      <div className="m-page m-pair-page">
        <div className="m-pair-card">
          <Sparkles size={24} />
          <h1>连接余白</h1>
          <p>在电脑的驻场作家页面生成配对码，输入到这里。配对码 10 分钟内有效，只用一次。</p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="8 位配对码"
            autoCapitalize="characters"
            maxLength={8}
          />
          <input value={deviceName} onChange={(e) => setDeviceName(e.target.value)} placeholder="设备名" />
          {error && <div className="m-error">{error}</div>}
          <button className="m-primary" disabled={pairing || code.trim().length < 4} onClick={() => void pair()}>
            {pairing ? <Loader2 size={16} className="spin" /> : null} 配对
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="m-page">
      <header className="m-header">
        <div className="m-brand"><Sparkles size={16} /> 余白</div>
        <div className="m-book">
          <BookOpen size={14} />
          <select value={bookId} onChange={(e) => setBookId(e.target.value)}>
            {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select>
        </div>
        {typeof window !== 'undefined' && window.XiaotianshuAndroid && (
          <button className="m-icon" title="服务器设置" onClick={() => window.XiaotianshuAndroid?.openSettings()}>
            <Settings size={16} />
          </button>
        )}
        <button className="m-icon" title="断开此设备" onClick={() => void (async () => {
          clearMobileToken();
          setToken('');
          setCookieAuth(false);
          setBooks([]);
          setBookId('');
          await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => { /* ignore */ });
        })()}><LogOut size={16} /></button>
      </header>

      {error && <div className="m-error">{error}</div>}

      {notice && (
        <div className="m-notice" onClick={() => { setTab('chat'); setNotice(null); }}>
          <span>{notice.title}</span>
          <button className="m-notice-close" onClick={(e) => { e.stopPropagation(); setNotice(null); }}>×</button>
        </div>
      )}

      <main className="m-main">
        {tab === 'chat' && (
          <div className="m-messages" ref={messagesRef} onScroll={onMessagesScroll}>
            {messages.length === 0 && !streaming && (
              <div className="m-empty">和余白说点什么。她读过这本书，会先给判断，再动手。</div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`m-msg-row ${m.role === 'user' ? 'is-user' : 'is-agent'}`}>
                <div className="m-msg-time">{time(m.created_at)}</div>
                <div className={`m-bubble ${m.role === 'user' ? 'is-user' : 'is-agent'}`}>
                  <div className="m-bubble-text"><LinkifiedText text={m.text} /></div>
                  {m.images?.length ? (
                    <div className="m-bubble-images">
                      {m.images.map((img, index) => (
                        <img
                          key={index}
                          src={img.dataUrl}
                          alt={img.name ?? '图片'}
                          onClick={() => setImageViewer({ src: img.dataUrl, name: img.name })}
                        />
                      ))}
                    </div>
                  ) : null}
                  {m.documents?.length ? (
                    <div className="m-bubble-documents">
                      {m.documents.map((doc, index) => (
                        <span key={index}>
                          {doc.mimeType === 'application/pdf' || /\.pdf$/i.test(doc.name) ? <FileText size={10} /> : <Paperclip size={10} />} {doc.name}
                          {doc.dataUrl && (
                            <button
                              className="m-doc-download"
                              onClick={() => saveChatFile(doc.dataUrl!, doc.name, doc.mimeType)}
                            >
                              <Download size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {streaming && <div className="m-msg-row is-agent"><div className="m-msg-time">正在写…</div><div className="m-bubble is-agent"><div className="m-bubble-text">{streaming}</div></div></div>}
          </div>
        )}

        {tab === 'feed' && (
          <div className="m-list">
            <button className="m-refresh" onClick={() => { void refreshIdeas(bookId); void refreshDrafts(bookId); }}>
              <RefreshCw size={14} /> 刷新
            </button>
            {feed.length === 0 && <p className="m-muted">还没有想法或草案。</p>}
            {feed.map((item) => item.kind === 'idea' ? (
              <article
                key={item.idea.id}
                className={`m-card m-feed-card${expandedFeedId === `idea:${item.idea.id}` ? ' is-expanded' : ''}`}
                onClick={(e) => toggleFeed(`idea:${item.idea.id}`, e)}
              >
                <div className="m-feed-top">
                  <span className="m-tag idea">想法</span>
                  {item.idea.status === 'approved' && <span className="m-tag ok">已认可</span>}
                  {item.idea.status === 'rejected' && <span className="m-tag muted">已拒绝</span>}
                  <span className="m-tag time">{shortTime(item.created)}</span>
                </div>
                <h3>{item.idea.title}</h3>
                {(item.idea.summary || item.idea.content || item.idea.rationale) && (
                  <p className="m-feed-preview">{item.idea.summary || item.idea.content || item.idea.rationale}</p>
                )}
                {expandedFeedId === `idea:${item.idea.id}` && (
                  <div className="m-feed-full">
                    {item.idea.content && item.idea.content !== item.idea.summary && (
                      <><h4>正文</h4><pre>{item.idea.content}</pre></>
                    )}
                    {item.idea.rationale && (
                      <><h4>理由</h4><p>{item.idea.rationale}</p></>
                    )}
                    <span className="m-feed-expand-hint">点击卡片收起</span>
                  </div>
                )}
                {item.idea.status === 'proposed' && rejectingIdeaId === item.idea.id && (
                  <div className="m-reject-box">
                    <textarea rows={2} value={rejectText} placeholder="为什么不合适？（可留空，余白会记住）" onChange={(e) => setRejectText(e.target.value)} />
                    <div className="m-actions">
                      <button className="m-primary small" onClick={() => void reject(item.idea)}>确认拒绝</button>
                      <button className="m-ghost small" onClick={() => setRejectingIdeaId(null)}>取消</button>
                    </div>
                  </div>
                )}
                {item.idea.status === 'proposed' && (
                  <div className="m-actions">
                    <button className="m-primary small" onClick={() => void approve(item.idea)}>认可</button>
                    <button className="m-ghost small" onClick={() => { setRejectingIdeaId(item.idea.id); setRejectText(''); }}>拒绝</button>
                  </div>
                )}
              </article>
            ) : (
              <article
                key={item.draft.id}
                className={`m-card m-feed-card${expandedFeedId === `draft:${item.draft.id}` ? ' is-expanded' : ''}`}
                onClick={(e) => toggleFeed(`draft:${item.draft.id}`, e)}
              >
                <div className="m-feed-top">
                  <span className="m-tag draft">草案</span>
                  {item.draft.status === 'rejected' && <span className="m-tag muted">已拒绝</span>}
                  <span className="m-tag time">{shortTime(item.created)}</span>
                  <span className="m-feed-meta">{item.draft.word_count} 字</span>
                </div>
                <h3>{item.draft.title ?? '未命名草案'}</h3>
                {(item.draft.summary || item.draft.content) && (
                  <p className="m-feed-preview">{item.draft.summary || item.draft.content}</p>
                )}
                {expandedFeedId === `draft:${item.draft.id}` && (
                  <div className="m-feed-full">
                    <pre>{item.draft.content}</pre>
                    <span className="m-feed-expand-hint">点击卡片收起</span>
                  </div>
                )}
                {item.draft.status === 'pending' && rejectingDraftId === item.draft.id && (
                  <div className="m-reject-box">
                    <textarea rows={2} value={draftRejectText} placeholder="给余白的意见（可留空）" onChange={(e) => setDraftRejectText(e.target.value)} />
                    <div className="m-actions">
                      <button className="m-primary small" onClick={() => void rejectDraft(item.draft)}>确认拒绝</button>
                      <button className="m-ghost small" onClick={() => setRejectingDraftId(null)}>取消</button>
                    </div>
                  </div>
                )}
                {item.draft.status === 'pending' && (
                  <div className="m-actions">
                    <button className="m-primary small" onClick={() => void accept(item.draft)}>接受并写入</button>
                    <button className="m-ghost small" onClick={() => { setRejectingDraftId(item.draft.id); setDraftRejectText(''); }}>拒绝</button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === 'works' && (
          <div className="m-list m-works">
            <button className="m-refresh" onClick={() => void refreshLibrary()}>
              <RefreshCw size={14} /> 刷新
            </button>
            {libraryLoading && <p className="m-muted"><Loader2 size={14} className="spin" /> 正在读取作品…</p>}
            {!libraryLoading && !library && <p className="m-muted">还没有读到作品。</p>}
            {library && (
              <>
                <div className="m-work-summary">
                  <h2>{library.book.title}</h2>
                  <p>{library.book.word_count ?? 0} 字 · {library.volumes.reduce((n, v) => n + v.chapters.length, 0)} 章</p>
                </div>
                {library.volumes.map((volume) => (
                  <section key={volume.id} className="m-volume">
                    <h3><Layers size={14} /> {volume.title}</h3>
                    {volume.description && <p className="m-muted">{volume.description}</p>}
                    {volume.chapters.map((chapter) => (
                      <button key={chapter.id} className="m-row" onClick={() => void openChapter(chapter)}>
                        <FileText size={15} />
                        <span>第 {chapter.chapter_number ?? '?'} 章 {chapter.title}</span>
                        <small>{chapter.word_count ?? 0} 字</small>
                      </button>
                    ))}
                  </section>
                ))}
                <h3 className="m-section-title"><Users size={14} /> 资料库</h3>
                <div className="m-data-group">
                  <h4>人物</h4>
                  {library.library.characters.map((c) => (
                    <button key={c.id} className="m-row" onClick={() => setDetail({ kind: 'character', title: c.name, body: [c.age && `年龄：${c.age}`, c.strength && `实力：${c.strength}`, c.description].filter(Boolean).join('\n') })}>
                      <span>{c.name}</span><small>{c.strength || ''}</small>
                    </button>
                  ))}
                </div>
                <div className="m-data-group">
                  <h4>势力</h4>
                  {library.library.factions.map((f) => (
                    <button key={f.id} className="m-row" onClick={() => setDetail({ kind: 'faction', title: f.name, body: f.description || '' })}>
                      <span>{f.name}</span>
                    </button>
                  ))}
                </div>
                <div className="m-data-group">
                  <h4>设定</h4>
                  {library.library.settings.map((s) => (
                    <button key={s.id} className="m-row" onClick={() => setDetail({ kind: 'setting', title: s.title, body: s.content || '' })}>
                      <span>{s.title}</span>
                    </button>
                  ))}
                </div>
                <div className="m-data-group">
                  <h4>物品</h4>
                  {library.library.items.map((it) => (
                    <button key={it.id} className="m-row" onClick={() => setDetail({ kind: 'item', title: it.name, body: [it.type, it.description].filter(Boolean).join('\n') })}>
                      <span>{it.name}</span><small>{it.type}</small>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {tab === 'chat' && showJump && (
          <button className="m-jump" onClick={() => scrollToLatest(false)}>↓ 回到最新</button>
        )}
      </main>

      {approval && (
        <div className="m-approval-overlay">
          <div className="m-approval-dialog" role="dialog" aria-modal="true">
            <ApprovalDetails approval={approval} lookupTarget={lookupApprovalTarget} />
            <div className="m-actions">
              <button className="m-primary small" disabled={busy} onClick={() => void answerApproval('allow')}>允许这一次</button>
              <button className="m-ghost small" disabled={busy} onClick={() => void answerApproval('deny')}>拒绝</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="m-detail-sheet">
          <header>
            <button className="m-icon" onClick={() => setDetail(null)}><ChevronLeft size={18} /></button>
            <strong>{detail.title}</strong>
            <span />
          </header>
          <pre>{detail.body}</pre>
        </div>
      )}

      {tab === 'chat' && (
        <>
          {activity && (
            <div className="m-activity"><span className="m-activity-dot" /> {activity}</div>
          )}
          <div className="m-composer-wrap">
          {(pendingImages.length > 0 || pendingDocuments.length > 0) && (
            <div className="m-composer-images">
              {pendingImages.map((img, index) => (
                <div key={index} className="m-composer-image">
                  <img src={img.dataUrl} alt={img.name ?? '待发送图片'} />
                  <button onClick={() => setPendingImages((prev) => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
              {pendingDocuments.map((doc, index) => (
                <div key={index} className="m-pending-doc">
                  {doc.mimeType === 'application/pdf' || /\.pdf$/i.test(doc.name) ? <FileText size={12} /> : <Paperclip size={12} />}
                  <span>{doc.name}</span>
                  <button onClick={() => setPendingDocuments((prev) => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
            </div>
          )}
          <div className="m-composer">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              hidden
              onChange={(e) => void addImages(e.target.files)}
            />
            <input
              ref={documentInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => void addDocuments(e.target.files)}
            />
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              placeholder="和余白说点什么…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            />
            <div className="m-composer-actions">
              <div className="m-attach-group">
                <button className="m-attach" title="发送图片" disabled={running || pendingImages.length >= 4} onClick={() => imageInputRef.current?.click()}>
                  <ImagePlus size={19} />
                </button>
                <button className="m-attach" title="发送文档" disabled={running || pendingDocuments.length >= 4} onClick={() => documentInputRef.current?.click()}>
                  <Paperclip size={18} />
                </button>
              </div>
              <button className="m-primary" disabled={running || (!input.trim() && pendingImages.length === 0 && pendingDocuments.length === 0)} onClick={() => void send()}>
                <Send size={16} />
              </button>
            </div>
          </div>
          </div>
        </>
      )}

      <nav className="m-tabs">
        <button className={tab === 'chat' ? 'is-active' : ''} onClick={() => setTab('chat')}>聊天</button>
        <button className={tab === 'feed' ? 'is-active' : ''} onClick={() => setTab('feed')}>想法/草案 {ideas.filter((i) => i.status === 'proposed').length + drafts.filter((d) => d.status === 'pending').length || ''}</button>
        <button className={tab === 'works' ? 'is-active' : ''} onClick={() => setTab('works')}>作品</button>
      </nav>
      {imageViewer && (
        <ImageViewer src={imageViewer.src} name={imageViewer.name} onClose={() => setImageViewer(null)} />
      )}
    </div>
  );
}

